import React, { useState } from "react";
import { request } from "../services/utils";
import "./login.css";
import { useNotification } from "../contexts/NotificationContext";

const PASSWORD_MIN_LENGTH = 8;
// At least one uppercase letter, one digit, and one special (non-alphanumeric) character.
// Mirrors the P4 User password complexity rules (DPP-1380).
const PASSWORD_COMPLEXITY = /^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).+$/;

// Per-rule checks driving the live requirements checklist. Kept in lockstep
// with PASSWORD_COMPLEXITY / PASSWORD_MIN_LENGTH above.
const PASSWORD_REQUIREMENTS = [
    { label: "At least 8 characters", test: (pw) => pw.length >= PASSWORD_MIN_LENGTH },
    { label: "One uppercase letter", test: (pw) => /[A-Z]/.test(pw) },
    { label: "One number", test: (pw) => /\d/.test(pw) },
    { label: "One special character", test: (pw) => /[^A-Za-z0-9]/.test(pw) },
];

function passwordValidationError(password, confirmation) {
    if (password.length < PASSWORD_MIN_LENGTH) {
        return "Password must be at least 8 characters long";
    }
    if (!PASSWORD_COMPLEXITY.test(password)) {
        return "Password must include at least one uppercase letter, one number, and one special character";
    }
    if (password !== confirmation) {
        return "Passwords do not match";
    }
    return null;
}

export default function ResetPassword({ history }) {
    const [isFetching, setIsFetching] = useState(false);
    const [token] = useState(
        () => new URLSearchParams(history.location.search).get("token") || ""
    );
    const [newPassword, setNewPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [pwFocused, setPwFocused] = useState(false);
    const { createErrorNotification, createNotification } = useNotification();

    const showRequirements = pwFocused || newPassword.length > 0;
    const isValid = passwordValidationError(newPassword, confirmPassword) === null;

    function handleSubmit(event) {
        event.preventDefault();
        const validationError = passwordValidationError(newPassword, confirmPassword);
        if (validationError) {
            createErrorNotification(`Error: ${validationError}`);
            return;
        }
        setIsFetching(true);
        request
            .patch(`/api/passwords/${encodeURIComponent(token)}`, { password: newPassword, passwordConfirmation: confirmPassword })
            .then(() => {
                createNotification("Password changed, please log in again using your new password");
                history.push("/login");
            })
            .catch((error) => {
                if (error.normalizedMessage?.includes('Invalid or expired token')) {
                    createErrorNotification("Reset link is invalid or expired. Please request a new one.");
                    history.push("/forgot-password");
                } else {
                    createErrorNotification(`Error: ${error?.normalizedMessage}. Try again or contact us.`);
                }
            })
            .finally(() => setIsFetching(false));
    }

    if (!token) {
        return (
            <div className="card">
                <p className="sign" align="center">Set a new password</p>
                <p className="please-wait-text" align="center">Invalid or missing reset token. Please request a new password reset link.</p>
                <button onClick={() => history.push("/login")} className="forgotpw" align="center">
                    Go back to Login
                </button>
            </div>
        );
    }

    return (
        <div className="card">
            <p className="sign" align="center">Set a new password</p>
            <form onSubmit={handleSubmit} className="form2" id="reset-form">
                <input
                    id="reset-form_new-password-input"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    name="pw"
                    className="pass"
                    type="password"
                    align="center"
                    placeholder="New password"
                    autoComplete="new-password"
                    onFocus={() => setPwFocused(true)}
                    onBlur={() => setPwFocused(false)}
                    required
                />
                {showRequirements && (
                    <ul className="pw-requirements" id="reset-form_pw-requirements">
                        {PASSWORD_REQUIREMENTS.map(({ label, test }) => {
                            const met = test(newPassword);
                            return (
                                <li key={label} className={met ? "met" : ""}>
                                    <span className="pw-req-icon">{met ? "✓" : "○"}</span>
                                    {label}
                                </li>
                            );
                        })}
                        <li className={confirmPassword.length > 0 && newPassword === confirmPassword ? "met" : ""}>
                            <span className="pw-req-icon">
                                {confirmPassword.length > 0 && newPassword === confirmPassword ? "✓" : "○"}
                            </span>
                            Passwords match
                        </li>
                    </ul>
                )}
                <input
                    id="reset-form_confirm-password-input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    name="pwConfirm"
                    className="pass"
                    type="password"
                    align="center"
                    placeholder="Confirm password"
                    autoComplete="new-password"
                    required
                />
                {isFetching ? (
                    <p className="please-wait-text">Please wait...</p>
                ) : (
                    <button type="submit" value="reset" id="submit" className="submit" align="center" disabled={!isValid}>
                        Confirm
                    </button>
                )}
            </form>
            <button disabled={isFetching} onClick={() => history.push("/login")} className="forgotpw" align="center">
                Go back to Login
            </button>
        </div>
    );
}
